import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Star, CheckCircle2, Sparkles, Volume2, Disc3 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import JsonLd from "../components/JsonLd";
import { QUOTE_URL, IMAGES, SERVICES, HERO_SLIDES, CONTACT, imgSrcSet } from "../data/site";
import SEO_HOME from "../data/seo-home.json";

const CORP_HOLIDAY_IMG = "/corporate-holiday.webp";

const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: SEO_HOME.faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const EASE = [0.25, 0.1, 0.25, 1];
const SLIDE_INTERVAL = 6500; // ms between slide transitions

function Hero() {
  const ref = useRef(null);
  const [slide, setSlide] = useState(0);
  const [ready, setReady] = useState(false);
  // Only mount slides 2–5 in the DOM after LCP is comfortably done.
  // This keeps the initial payload at ~250 KB (slide 1) instead of ~1.1 MB.
  const [mountRest, setMountRest] = useState(false);

  useEffect(() => {
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1400));
    const handle = schedule(() => setMountRest(true), { timeout: 2500 });
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(handle);
      else clearTimeout(handle);
    };
  }, []);

  // Pre-decode every hero image AFTER mountRest fires (Safari flash fix).
  useEffect(() => {
    if (!mountRest) return;
    let cancelled = false;
    Promise.all(
      HERO_SLIDES.map((s) => {
        const img = new Image();
        img.src = s.src;
        return (img.decode ? img.decode() : Promise.resolve()).catch(() => {});
      })
    ).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, [mountRest]);

  // Only start the auto-advance timer once every slide is decoded.
  useEffect(() => {
    if (!ready) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), SLIDE_INTERVAL);
    return () => clearInterval(id);
  }, [ready]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <div className="absolute inset-0">
        {HERO_SLIDES.map((s, i) => {
          // Slide 1 always renders (it's the LCP). Slides 2–5 wait for idle.
          if (i > 0 && !mountRest) return null;
          return (
            <img
              key={s.src}
              src={s.src}
              srcSet={s.srcMobile ? `${s.srcMobile} 800w, ${s.src} 1600w` : undefined}
              sizes={s.srcMobile ? "100vw" : undefined}
              alt={s.alt}
              width="1600"
              height="1067"
              loading="eager"
              fetchPriority={i === 0 ? "high" : "low"}
              decoding={i === 0 ? "sync" : "async"}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${i === slide ? "opacity-100" : "opacity-0"}`}
              style={i === 0 ? undefined : {
                transform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                willChange: "opacity",
              }}
            />
          );
        })}
        <div className="absolute inset-0 bg-black/15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(22_26%_10%/0.25)] via-[hsl(22_26%_12%/0.45)] to-[hsl(22_26%_12%/0.85)]" />
        <div className="absolute inset-0 bg-[hsl(351_48%_80%/0.10)] mix-blend-soft-light" />
      </div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pt-44 pb-16 md:pt-56 md:pb-24">
        <h1 className="font-serif text-[hsl(30_40%_97%)] text-4xl sm:text-6xl md:text-7xl lg:text-[6.5rem] leading-[1.14] md:leading-[1.1] tracking-tight max-w-5xl text-shadow-hero-strong">
          Where every moment <span className="italic gold-gradient-text">sparkles</span>
        </h1>

        <h2 className="hero-fade-up mt-6 md:mt-12 max-w-xl font-cormorant font-normal text-base md:text-2xl text-[hsl(30_30%_88%)] leading-relaxed text-shadow-hero" style={{ animationDelay: "0.95s" }}>
          Abilene wedding DJs, breathtaking event decor, and flawless coordination —
          crafted to make your celebration effortlessly unforgettable. Now booking 2026 &amp; 2027 weddings.
        </h2>

        <div className="hero-fade-up mt-6 md:mt-10 flex flex-wrap gap-3 md:gap-4" style={{ animationDelay: "1.15s" }}>
            <a
              href={QUOTE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-quote-cta"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient text-espresso px-8 py-4 text-sm uppercase tracking-[0.16em] font-medium transition-transform duration-300 hover:scale-[1.04]"
            >
              Get an Instant Quote
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/consultation"
              data-testid="hero-consult-cta"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border-2 border-[hsl(30_40%_90%/0.7)] text-[hsl(30_40%_97%)] px-8 py-4 text-sm uppercase tracking-[0.16em] backdrop-blur-sm shadow-[0_8px_24px_rgba(0,0,0,0.3)] transition-all duration-300 hover:shadow-[0_14px_36px_rgba(0,0,0,0.45)] hover:scale-[1.04] hover:bg-[hsl(30_40%_97%/0.12)]"
            >
              Schedule a Consultation
            </Link>
          </div>
      </div>
    </section>
  );
}

const GoldStar = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <defs>
      <linearGradient id="goldMetalStar" x1="0%" y1="20%" x2="100%" y2="80%">
        <stop offset="0%" stopColor="hsl(34, 45%, 42%)" />
        <stop offset="50%" stopColor="hsl(38, 58%, 66%)" />
        <stop offset="100%" stopColor="hsl(34, 45%, 46%)" />
      </linearGradient>
    </defs>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#goldMetalStar)" />
  </svg>
);

const MARQUEE_WORDS = ["Elegant", "Unforgettable", "Premium", "Exclusive", "Timeless"];

const MarqueeGroup = () => (
  <div className="marquee__group" aria-hidden="true">
    {MARQUEE_WORDS.map((t, i) => (
      <span key={`${t}-${i}`} className="flex items-center shrink-0">
        <span className="font-serif italic text-2xl md:text-5xl leading-[1.4] pb-2 text-[hsl(22_20%_24%)] px-6 md:px-8">{t}</span>
        <GoldStar size={16} />
      </span>
    ))}
  </div>
);

function MarqueeBand() {
  return (
    <div data-testid="hero-marquee" className="py-5 md:py-10 bg-blush-soft border-y border-[hsl(var(--border))] overflow-hidden">
      <div className="marquee">
        <MarqueeGroup />
        <MarqueeGroup />
      </div>
      <span className="sr-only">{MARQUEE_WORDS.join(" · ")}</span>
    </div>
  );
}

const CHAPTERS = [
  { n: "01", t: "We listen first", d: "Every couple has a story. Before a single song is chosen, we learn yours — the moments that matter, the feeling you want your guests to carry home, and the small details that make the whole night sparkle." },
  { n: "02", t: "We design the details", d: "From florals and lighting to a precisely-timed run of show, we compose an atmosphere where nothing feels forced and everything feels like you." },
  { n: "03", t: "We make it effortless", d: "On the day itself, you're a guest at your own celebration. We handle the vendors, the timeline, the transitions — you simply enjoy every second." },
];

function Manifesto() {
  return (
    <section data-testid="manifesto-section" className="relative bg-espresso text-[hsl(30_35%_92%)] py-12 md:py-24 grain overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="eyebrow mb-4 md:mb-5">The Philosophy</p>
          <h2 className="font-serif text-3xl md:text-6xl leading-[1.1] md:leading-[1.05] tracking-tight max-w-3xl text-[hsl(30_40%_97%)]">
            A celebration should feel like <span className="italic gold-gradient-text">a memory</span> before it even begins.
          </h2>
        </Reveal>

        <div className="mt-10 md:mt-14 grid md:grid-cols-3 gap-x-10 gap-y-8 md:gap-y-12">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12}>
              <div className="group">
                <span className="font-serif text-5xl md:text-6xl text-[hsl(var(--gold)/0.55)] transition-colors duration-500 group-hover:text-[hsl(var(--gold-soft))]">{c.n}</span>
                <div className="hairline my-4 md:my-6" />
                <p className="font-serif text-xl md:text-2xl mb-2 md:mb-3 text-[hsl(30_40%_97%)]">{c.t}</p>
                <p className="text-[hsl(30_22%_74%)] leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSnippet() {
  return (
    <section data-testid="home-services" className="py-12 md:py-20 bg-[hsl(24_28%_95%)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-10 md:mb-14">
          <Reveal>
            <p className="eyebrow mb-4 md:mb-5">What We Do</p>
            <h2 className="font-serif text-3xl md:text-6xl tracking-tight">We do it all.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/services" data-testid="home-services-link" className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-espresso link-underline">
              All Services <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-12 gap-4 md:gap-5">
          {SERVICES.map((s, i) => {
            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
            return (
              <Reveal key={s.key} delay={i * 0.08} className={spans[i]}>
                <Link to="/services" className="group relative block h-56 md:h-80 rounded-2xl overflow-hidden">
                  <img src={s.image} srcSet={imgSrcSet(s.image)} sizes="(min-width: 768px) 620px, calc(100vw - 48px)" alt={s.alt || s.title} width="800" height="600" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(22_26%_12%/0.82)] via-[hsl(22_26%_12%/0.25)] to-transparent" />
                  <div className="absolute inset-0 p-5 md:p-7 flex flex-col justify-end">
                    <span className="font-serif text-[hsl(var(--gold-soft))] text-sm mb-1">{s.number}</span>
                    <p className="font-serif text-xl md:text-3xl text-[hsl(30_40%_97%)]">{s.title}</p>
                    <p className="text-[hsl(30_30%_86%)] text-sm mt-1 opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">{s.tagline}</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ClosingCTA() {
  return null;
}

const WHY_US = [
  {
    t: "One team, one timeline",
    d: "Your DJ, coordinator, and decor designer all work under one roof. That means your music cues, your walkthrough, and your florals stay perfectly in sync — no crossed wires, no dead air, no vendors blaming each other on the day of your wedding.",
    to: "/services",
    linkLabel: "See what's included",
  },
  {
    t: "20+ years in the Big Country",
    d: "We've performed at hundreds of Abilene weddings, corporate galas, quinceañeras, and festivals. We know every venue's quirks, every backroad, and every load-in door — so setup is smooth and every ceremony starts on time.",
    to: "/about",
    linkLabel: "Meet the team",
  },
  {
    t: "Reviewed by real couples",
    d: "Class and style aren't marketing words — they're what our couples actually write about us. Read the reviews before you book anyone else in Abilene, and see why brides and grooms keep sending their friends our way.",
    to: "/testimonials",
    linkLabel: "Read testimonials",
  },
  {
    t: "Transparent, itemized pricing",
    d: "No hidden fees, no bait-and-switch, no vague packages. Use our instant quote builder to see exactly what's included at every tier, then customize until it fits your vision and your budget.",
    href: QUOTE_URL,
    linkLabel: "Get your instant quote",
  },
];

function WhyChooseUs() {
  return (
    <section data-testid="why-choose-us" className="py-14 md:py-28 bg-blush-soft">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-6 md:gap-16 mb-10 md:mb-14">
          <Reveal className="md:col-span-5">
            <p className="eyebrow mb-4 md:mb-5">Why couples choose us</p>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.1] md:leading-[1.05] tracking-tight">
              Abilene's wedding pros, <span className="italic gold-gradient-text">under one roof.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:pt-4">
            <p className="text-base md:text-xl text-[hsl(22_20%_24%)] leading-relaxed">
              Since 2007, Pro DJ's &amp; Event Services has been Abilene's home for wedding entertainment,
              coordination, and decor. Because everything happens under one roof, your day flows without
              the vendor-to-vendor friction most couples fight through — and you get to be a guest at
              your own celebration. Whether you're planning a downtown loft ceremony, a ranch reception,
              or a full-blown black-tie affair, we've done it, and we can't wait to do yours.
            </p>
            <p data-testid="why-parents-line" className="mt-5 font-serif italic text-lg md:text-2xl text-espresso">
              Loved by couples — <span className="gold-gradient-text">and the parents behind them.</span>
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 md:gap-y-12">
          {WHY_US.map((item, i) => (
            <Reveal key={item.t} delay={i * 0.08}>
              <div>
                <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                  <span className="mt-1 inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-full bg-espresso text-[hsl(var(--gold-soft))] shrink-0">
                    <CheckCircle2 size={16} />
                  </span>
                  <p className="font-serif text-xl md:text-3xl leading-tight">{item.t}</p>
                </div>
                <p className="text-[hsl(22_20%_28%)] leading-relaxed pl-10 md:pl-12">{item.d}</p>
                <div className="pl-10 md:pl-12 mt-3 md:mt-4">
                  {item.to ? (
                    <Link
                      to={item.to}
                      data-testid={`why-link-${i}`}
                      className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-espresso link-underline"
                    >
                      {item.linkLabel}
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  ) : (
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`why-link-${i}`}
                      className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-espresso link-underline"
                    >
                      {item.linkLabel}
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const CITIES = [
  "Abilene", "Buffalo Gap", "Tuscola", "Merkel", "Hawley", "Anson",
  "Sweetwater", "Snyder", "Brownwood", "Baird", "Clyde", "Coleman",
  "Ballinger", "Winters", "Roscoe", "Stamford",
];

const PARENT_POINTS = [
  "One team for DJ, coordination & décor — no crossed wires, no vendor finger-pointing.",
  "On-time and on-schedule — every toast, every dance, every announcement handled.",
  "Transparent pricing and a clear plan, so there are no day-of surprises.",
];

function ParentsPeaceOfMind() {
  return (
    <section data-testid="parents-peace-of-mind" className="py-14 md:py-24 bg-[hsl(24_28%_95%)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-8 md:gap-14 items-center">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow mb-4 md:mb-5">Peace of Mind for Families</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] md:leading-[1.05] tracking-tight">
              When it's your family's celebration, <span className="italic gold-gradient-text">the details matter.</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[hsl(22_20%_26%)] leading-relaxed">
              Planning your son or daughter's big day — or footing the bill for it? We make the whole day
              effortless for the entire family, so you can step back and actually enjoy it.
            </p>
            <ul className="mt-7 space-y-4">
              {PARENT_POINTS.map((p, i) => (
                <li key={i} data-testid={`parents-point-${i}`} className="flex items-start gap-3 md:gap-4">
                  <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-espresso text-[hsl(var(--gold-soft))] shrink-0">
                    <CheckCircle2 size={15} />
                  </span>
                  <span className="text-[hsl(22_20%_26%)] leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/consultation"
                data-testid="parents-consult-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-espresso text-[hsl(30_40%_97%)] px-6 py-3 text-sm uppercase tracking-[0.16em] transition-transform duration-300 hover:-translate-y-0.5"
              >
                Schedule a Consultation
                <ArrowRight size={15} />
              </Link>
              <Link
                to="/blog/mother-of-the-bride-guide-stress-free-abilene-wedding"
                data-testid="parents-guide-link"
                className="inline-flex items-center justify-center gap-2 text-sm uppercase tracking-[0.16em] text-espresso link-underline"
              >
                Read the parents' guide
                <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-6">
            <figure className="relative bg-espresso grain rounded-2xl p-8 md:p-12 border border-[hsl(var(--gold)/0.3)] shadow-[0_24px_60px_rgba(0,0,0,0.18)]">
              <div className="flex gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={16} className="text-[hsl(var(--gold-soft))] fill-[hsl(var(--gold-soft))]" />
                ))}
              </div>
              <blockquote className="font-cormorant text-xl md:text-2xl italic leading-snug text-[hsl(30_35%_92%)]">
                "He keeps the music flowing and we had people on the dance floor ALL NIGHT!! We received
                so many compliments on how fun it was. Thank you, Kody, for the best night!"
              </blockquote>
              <figcaption className="mt-6">
                <p className="text-sm uppercase tracking-[0.22em] text-[hsl(var(--gold-soft))]">Callie D.</p>
                <p className="mt-1.5 text-xs uppercase tracking-[0.16em] text-[hsl(30_20%_65%)]">Mother of the Bride · The Mill, Abilene</p>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AreasWeServe() {
  return (
    <section data-testid="areas-we-serve" className="py-14 md:py-24 bg-[hsl(24_28%_95%)]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 md:gap-16 items-start">
          <Reveal className="md:col-span-5">
            <p className="eyebrow mb-4 md:mb-5">Areas We Serve</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] md:leading-[1.05] tracking-tight">
              Proudly serving the <span className="italic gold-gradient-text">Big Country.</span>
            </h2>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
              Based in Abilene, Texas, we travel throughout the Big Country and beyond for weddings,
              corporate events, festivals, and private celebrations. We know the region's best venues —
              from historic downtown ballrooms to sprawling ranch properties — and we come prepared for
              every one of them.
            </p>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed">
              Don't see your city on the list? We travel across West Texas.{" "}
              <Link to="/contact" data-testid="areas-contact-link" className="link-underline text-espresso">
                Send us a message
              </Link>{" "}
              or{" "}
              <Link to="/consultation" data-testid="areas-consult-link" className="link-underline text-espresso">
                book a free consultation
              </Link>{" "}
              and we'll confirm availability for your date.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="md:col-span-7">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 md:gap-x-6 gap-y-2 md:gap-y-3">
              {CITIES.map((c) => (
                <div
                  key={c}
                  className="flex items-center gap-2 md:gap-2.5 text-[hsl(22_20%_24%)] text-sm md:text-base"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--gold))]" />
                  {c}, TX
                </div>
              ))}
            </div>

            <div className="mt-8 md:mt-10 p-5 md:p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(30_40%_97%)]">
              <p className="font-serif text-lg md:text-xl mb-2">Just getting started?</p>
              <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] leading-relaxed mb-3 md:mb-4">
                Grab our free, month-by-month checklist — the same framework we walk our own couples
                through, from 12 months out to the final week.
              </p>
              <Link
                to="/wedding-planning-checklist"
                data-testid="areas-checklist-link"
                className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-espresso link-underline"
              >
                Open the free planning checklist
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const JOURNAL_POSTS = [
  {
    slug: "ultimate-wedding-reception-timeline",
    title: "The ultimate wedding reception timeline",
    excerpt: "A minute-by-minute breakdown of a flawless reception — from grand entrance to send-off.",
  },
  {
    slug: "questions-to-ask-before-booking-wedding-dj",
    title: "10 questions to ask before booking a wedding DJ",
    excerpt: "The questions that separate real professionals from weekend hobbyists.",
  },
  {
    slug: "how-a-wedding-coordinator-makes-your-day-stress-free",
    title: "How a wedding coordinator makes your day stress-free",
    excerpt: "What a coordinator actually does — and why every couple should hire one.",
  },
];

function JournalPreview() {
  return (
    <section data-testid="home-journal" className="py-14 md:py-24 bg-espresso text-[hsl(30_35%_92%)] grain relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 mb-10 md:mb-14">
          <Reveal>
            <p className="eyebrow mb-4 md:mb-5 text-[hsl(var(--gold-soft))]">From the Journal</p>
            <h2 className="font-serif text-3xl md:text-6xl leading-[1.1] md:leading-[1.05] tracking-tight text-[hsl(30_40%_97%)]">
              Real advice, <span className="italic gold-gradient-text">from working pros.</span>
            </h2>
            <p className="mt-4 md:mt-6 max-w-2xl text-sm md:text-base text-[hsl(30_25%_78%)] leading-relaxed">
              We write everything ourselves — no filler, no fluff, no AI recycling. If you're planning
              your wedding, these are the guides we hand our own couples.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/blog"
              data-testid="journal-all-link"
              className="group inline-flex items-center gap-2 text-sm uppercase tracking-[0.16em] text-[hsl(var(--gold-soft))] link-underline"
            >
              Read the Journal
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="-mx-6 md:mx-0 px-6 md:px-0 flex md:grid md:grid-cols-3 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory scroll-smooth pb-3 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {JOURNAL_POSTS.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08} className="snap-start shrink-0 w-[85%] md:w-auto md:shrink">
              <article
                className="group relative block h-full p-5 md:p-7 rounded-2xl border border-[hsl(30_20%_28%)] hover:border-[hsl(var(--gold)/0.5)] transition-colors duration-500"
              >
                <p className="eyebrow mb-3 md:mb-4 text-[hsl(var(--gold-soft))] opacity-90">Guide</p>
                <p className="font-serif text-xl md:text-2xl leading-tight text-[hsl(30_40%_97%)] mb-3 md:mb-4">
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`journal-post-${i}`}
                    aria-label={`Read the guide: ${p.title}`}
                    className="after:absolute after:inset-0 after:content-['']"
                  >
                    {p.title}
                  </Link>
                </p>
                <p className="text-sm md:text-base text-[hsl(30_25%_78%)] leading-relaxed mb-4 md:mb-6">{p.excerpt}</p>
                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[hsl(var(--gold-soft))]">
                  Read more
                  <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </article>
            </Reveal>
          ))}
        </div>

        {/* Mobile swipe hint (hidden on md+ where all 3 are visible) */}
        <p data-testid="journal-swipe-hint" className="md:hidden mt-3 text-xs uppercase tracking-[0.16em] text-[hsl(30_20%_60%)] flex items-center gap-2">
          Swipe for more <ArrowRight size={12} />
        </p>
      </div>
    </section>
  );
}

const FAQS = [
  {
    q: "How far in advance should we book an Abilene wedding DJ?",
    a: (
      <>
        The best DJs in the Big Country typically book 12–18 months out for peak season Saturdays. If
        you have a firm date and venue,{" "}
        <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="link-underline text-espresso">
          grab an instant quote
        </a>{" "}
        as soon as you can — we'll hold your date while you finalize details.
      </>
    ),
  },
  {
    q: "Do you handle wedding coordination separately from DJ services?",
    a: (
      <>
        Yes — we book DJ, coordination, and decor together or independently. Most couples save when they
        bundle. See what each package includes on our{" "}
        <Link to="/services" className="link-underline text-espresso">services page</Link>.
      </>
    ),
  },
  {
    q: "What areas do you travel to?",
    a: (
      <>
        We're based in Abilene and regularly travel across the Big Country — from Sweetwater and Snyder
        to Brownwood, Coleman, and beyond. Longer-distance weddings are welcome; travel is priced
        transparently in your quote.
      </>
    ),
  },
  {
    q: "Can we meet before we book?",
    a: (
      <>
        Absolutely — and we recommend it. A free 30-minute consultation is the best way to see if we're
        the right fit for your vision.{" "}
        <Link to="/consultation" className="link-underline text-espresso">
          Schedule a consultation
        </Link>{" "}
        and we'll bring the coffee.
      </>
    ),
  },
  {
    q: "Do you provide ceremony sound and wireless mics?",
    a: (
      <>
        Yes — for a small additional fee. We bring a dedicated second sound system for your ceremony
        (with wireless lapel and handheld mics) that runs independently from the reception rig, so the
        transition from vows to cocktail hour to reception is seamless — no gap in the music, no
        awkward silence while gear is moved. Ask about adding it when you{" "}
        <a href={QUOTE_URL} target="_blank" rel="noopener noreferrer" className="link-underline text-espresso">
          build your quote
        </a>.
      </>
    ),
  },
];

function HomeFAQ() {
  return (
    <section data-testid="home-faq" className="py-14 md:py-24 bg-blush-soft">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="eyebrow mb-4 md:mb-5">Frequently Asked</p>
          <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.1] md:leading-[1.05] tracking-tight">
            The questions we <span className="italic gold-gradient-text">hear the most.</span>
          </h2>
        </Reveal>

        <div className="mt-10 md:mt-14 space-y-5 md:space-y-8">
          {FAQS.map((f, i) => (
            <Reveal key={f.q} delay={i * 0.05}>
              <div data-testid={`faq-${i}`} className="border-b border-[hsl(var(--border))] pb-5 md:pb-8">
                <h3 className="font-serif text-lg md:text-2xl mb-2 md:mb-3 text-espresso">{f.q}</h3>
                <p className="text-[hsl(22_20%_28%)] leading-relaxed text-sm md:text-lg">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 md:mt-12 text-center">
            <p className="font-cormorant text-lg md:text-2xl text-[hsl(var(--muted-foreground))] mb-5 md:mb-6">
              Still have questions? Call or text {CONTACT.phone}.
            </p>
            <Link
              to="/consultation"
              data-testid="faq-consult-cta"
              className="inline-flex items-center gap-2 rounded-full bg-espresso text-[hsl(var(--primary-foreground))] px-7 md:px-8 py-3.5 md:py-4 text-sm uppercase tracking-[0.16em] transition-transform duration-300 hover:scale-[1.04]"
            >
              Schedule a Free Consultation
              <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TrustBand() {
  const stats = [
    { n: "2007", l: "Serving the Big Country" },
    { n: "20+", l: "Years of experience" },
    { n: "MC²", l: "Companies family" },
    { n: "2026 · 2027", l: "Now booking" },
  ];
  return (
    <section data-testid="trust-band" className="bg-[hsl(24_28%_95%)] border-b border-[hsl(var(--border))] py-8 md:py-10">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-7 gap-x-6 md:gap-x-4 text-center">
          {stats.map((s) => (
            <div key={s.l} className="flex flex-col items-center gap-1.5">
              <span className="font-serif text-2xl md:text-4xl gold-gradient-text leading-none">{s.n}</span>
              <span className="text-[10px] md:text-xs uppercase tracking-[0.12em] md:tracking-[0.18em] leading-snug text-[hsl(22_20%_28%)]">{s.l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CorporateHolidayBanner() {
  return (
    <section
      data-testid="holiday-banner"
      className="bg-espresso text-[hsl(30_40%_95%)] border-b border-[hsl(var(--gold)/0.2)]"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-2.5 md:py-3 flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-4 text-center">
        <span className="inline-flex items-center gap-2 text-xs md:text-sm">
          <Sparkles size={15} className="text-[hsl(var(--gold-soft))] shrink-0" />
          <span>
            <span className="font-medium text-[hsl(var(--gold-soft))]">Now booking corporate holiday parties</span>
            <span className="hidden sm:inline"> — reserve your date for the season.</span>
          </span>
        </span>
        <a
          href={QUOTE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="holiday-banner-quote"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-[hsl(var(--gold-soft))] link-underline whitespace-nowrap"
        >
          Get a Quote <ArrowRight size={13} />
        </a>
      </div>
    </section>
  );
}

function CorporateHolidayParties() {
  const features = [
    { icon: Disc3, label: "Pro DJ & MC" },
    { icon: Volume2, label: "Tour-grade sound" },
    { icon: Sparkles, label: "Full-service on request" },
  ];
  return (
    <section data-testid="corporate-holiday" className="bg-espresso grain relative overflow-hidden">
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <img
              src={CORP_HOLIDAY_IMG}
              alt="Pro DJ's & Event Services DJ booth and tour-grade sound system set up at an elegant Abilene corporate holiday party"
              loading="lazy"
              width="1264"
              height="848"
              className="w-full aspect-[4/3] object-cover rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
            />
          </Reveal>
          <Reveal delay={0.12}>
            <p className="eyebrow mb-4 text-[hsl(var(--gold-soft))]">Now Booking · Corporate Holiday Parties</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] text-[hsl(30_40%_97%)]">
              Your company holiday party, <span className="italic gold-gradient-text">done right.</span>
            </h2>
            <p className="mt-5 text-sm md:text-base text-[hsl(30_25%_80%)] leading-relaxed">
              The season fills up fast and the best dates go first. Lock in Abilene's premier DJ team now and give
              your team a night they'll be talking about long after the new year.
            </p>
            <p className="mt-4 text-sm md:text-base text-[hsl(30_25%_80%)] leading-relaxed">
              For corporate events we focus on what matters most — a world-class DJ and MC paired with a tour-grade
              sound system that keeps the energy high and every announcement crystal clear. Prefer to hand off more
              of the night? Our full event services — decor, planning, and vendor coordination — are available on request.
            </p>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
              {features.map((f) => (
                <span
                  key={f.label}
                  className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.14em] text-[hsl(30_30%_82%)]"
                >
                  <f.icon size={16} className="text-[hsl(var(--gold-soft))]" /> {f.label}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href={QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="corporate-quote-cta"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient text-espresso px-8 py-4 text-sm uppercase tracking-[0.16em] font-medium transition-transform duration-300 hover:scale-[1.04]"
              >
                Get an Instant Quote
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/consultation"
                data-testid="corporate-consult-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[hsl(30_40%_90%/0.5)] text-[hsl(30_40%_97%)] px-8 py-4 text-sm uppercase tracking-[0.16em] transition-all duration-300 hover:bg-[hsl(30_40%_97%/0.1)] hover:scale-[1.04]"
              >
                Schedule a Consultation
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <motion.div>
      <Seo description={SEO_HOME.description} path="/" />
      <JsonLd data={FAQ_LD} />
      <Hero />
      <CorporateHolidayBanner />
      <MarqueeBand />
      <TrustBand />
      <Manifesto />
      <ServicesSnippet />
      <WhyChooseUs />
      <ParentsPeaceOfMind />
      <CorporateHolidayParties />
      <AreasWeServe />
      <JournalPreview />
      <HomeFAQ />
    </motion.div>
  );
}
